import type { WatchPartyRecordingState, WatchPartyVisibility } from '../../types/auth';

/**
 * CORE-1526 (п. 9–10) — слова о состоянии записи, её аудитории и публикации.
 *
 * Аудит 28.09: внутри черновика пропадал контекст публикации — на странице
 * записи были «Поделиться» и удаление, но ни статуса «Черновик», ни аудитории,
 * ни публикации; а видимость «Публичный / Подписчики / Приватный» жила в одном
 * редакторе, публикация в «Медиа» — в другом меню, и связь между ними нигде
 * не объяснялась.
 *
 * Как это устроено на деле (см. `publishToMedia.ts`, CORE-588/CORE-115):
 * видимость — одна, у самой записи. Публикация в «Медиа» создаёт копию в
 * библиотеке практик, и та получает ту же видимость. Ниже — одни слова на
 * страницу записи, редактор архива и подсказки.
 */

export interface VisibilityCopy {
  label: string;
  /** Кому запись видна в списках и что происходит по прямой ссылке. */
  hint: string;
}

export const RECORDING_VISIBILITY: Record<WatchPartyVisibility, VisibilityCopy> = {
  Public: {
    label: 'Видна всем',
    hint: 'В общих списках и по ссылке — у любого, даже без входа в Agapella.',
  },
  Subscribers: {
    label: 'Только подписчики',
    hint: 'В списках и по ссылке — только у ваших подписчиков.',
  },
  Private: {
    label: 'Только вы',
    hint: 'В списках не показывается, по ссылке открывается только у вас.',
  },
};

/** Три состояния — старое булево `isPublic` у комнат до трёх видимостей. */
export function resolveVisibility(room: { visibility?: WatchPartyVisibility | null; isPublic?: boolean | null }): WatchPartyVisibility {
  if (room.visibility) return room.visibility;
  return room.isPublic === false ? 'Private' : 'Public';
}

export interface RecordingStatusCopy {
  label: string;
  tone: 'published' | 'draft' | 'processing' | 'failed';
}

/**
 * Статус записи одним словом — тем же, что в архиве «Записи Live-сессий»
 * (`statusChip`), чтобы черновик назывался черновиком на обеих страницах.
 */
export function recordingStatusCopy(
  state: WatchPartyRecordingState | null | undefined,
  published: boolean,
): RecordingStatusCopy {
  switch (state) {
    case 'Ready':
      return published
        ? { label: 'Опубликована в «Медиа»', tone: 'published' }
        : { label: 'Черновик', tone: 'draft' };
    case 'Processing':
      return { label: 'Обрабатывается', tone: 'processing' };
    case 'Recording':
      return { label: 'Идёт запись', tone: 'processing' };
    case 'Failed':
      return { label: 'Не записалась', tone: 'failed' };
    default:
      return { label: 'Не записывалась', tone: 'failed' };
  }
}

/** Где запись размещена — строка под статусом. */
export function placementLine(published: boolean): string {
  return published
    ? 'Есть в библиотеке «Медиа» с той же видимостью.'
    : 'В «Медиа» не опубликована — есть только на этой странице.';
}

/** Как связаны видимость и публикация — одной строкой рядом с выбором видимости. */
export const VISIBILITY_MEDIA_NOTE =
  'Видимость одна на запись: она действует здесь и переходит на копию в «Медиа» при публикации.';
